import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'

import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'

import { Atalho, formatarDia, hojeEmSaoPaulo, ROTULO_ATALHO } from '../Dashboards.periodo'
import { Perspectiva } from '../Dashboards.perspectivas'
import { DashboardsScope, Origem } from '../Dashboards.scopes'
import { ConsultasPainel } from './Consultas.view'
import { NegociosPainel } from './Negocios.view'
import { ParceirosPainel } from './Parceiros.view'
import { OperacaoPainel } from './Operacao.view'

const LOG = Logger.get('Dashboards-VIEW')

const useStyles = makeStyles()((theme) => ({
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(3),
        rowGap: theme.spacing(2.5),
        // O body do portal é fixo em 100vh, então o módulo precisa rolar por conta própria.
        overflow: 'auto',
        minHeight: 0,
        // Sem isto os cards encolhem até caber na coluna e recortam o próprio conteúdo (o Card do MUI tem
        // overflow: hidden).
        '& > *': {
            flexShrink: 0
        }
    },
    cabecalho: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap'
    },
    filtros: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap'
    },
    data: {
        width: 170
    },
    atualizado: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    }
}))

export type DashboardsViewProps = {
    className?: string
    scope: DashboardsScope
}

class DashboardsClass implements FCClassContext<DashboardsViewProps> {
    // :: Fields

    scope!: DashboardsScope
    styles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitAbaChange = (_evt: unknown, aba: Perspectiva) => this.scope.onAbaChange(aba).catch(LOG.caught)
    readonly emitAtalhoChange = (_evt: unknown, atalho: Atalho | null) => {
        // O ToggleButtonGroup manda null ao clicar no botão já escolhido.
        if (atalho) this.scope.onAtalhoChange(atalho).catch(LOG.caught)
    }
    readonly emitDeChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onDeChange(evt.target.value).catch(LOG.caught)
    readonly emitAteChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onAteChange(evt.target.value).catch(LOG.caught)
    readonly emitAtualizar = () => this.scope.onAtualizar().catch(LOG.caught)
    readonly emitBaixarPdf = () => this.scope.onBaixarPdf().catch(LOG.caught)
    readonly emitOrigemChange = (_evt: unknown, origem: Origem | null) => {
        if (origem) this.scope.onOrigemChange(origem).catch(LOG.caught)
    }

    // :: Methods

    /** A aba aberta já tem dados para o PDF. */
    temPainel() {
        const { scope } = this
        return Boolean(
            {
                operacao: scope.operacao,
                negocios: scope.negocios,
                parceiros: scope.parceiros,
                consultas: scope.consultas
            }[scope.aba]
        )
    }

    onSyncState({ scope }: DashboardsViewProps) {
        this.scope = scope
        this.styles = useStyles()
    }

    render({ className }: DashboardsViewProps) {
        const { scope } = this
        const { classes } = this.styles
        const perspectiva = scope.perspectivas.find((p) => p.id === scope.aba)

        return (
            <div className={clsx(classes.view, className)}>
                <Box className={classes.cabecalho}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Dashboards
                        </Typography>
                        <Typography color="text.secondary" fontSize={15}>
                            {perspectiva?.pergunta ?? 'Acompanhe o que acontece na LIA.'}
                        </Typography>
                    </Box>
                    {scope.perspectivas.length > 1 && (
                        <Tabs
                            value={scope.aba}
                            onChange={this.emitAbaChange}
                            aria-label="Perspectiva"
                            variant="scrollable"
                        >
                            {scope.perspectivas.map((p) => (
                                <Tab key={p.id} value={p.id} label={p.titulo} />
                            ))}
                        </Tabs>
                    )}
                </Box>

                {this.renderFiltros()}

                {scope.erroPeriodo && <Alert severity="warning">{scope.erroPeriodo}</Alert>}
                {scope.carregando && <LinearProgress aria-label="Carregando" />}

                {scope.aba === 'operacao' && scope.operacao && <OperacaoPainel scope={scope} />}
                {scope.aba === 'negocios' && scope.negocios && <NegociosPainel scope={scope} />}
                {scope.aba === 'parceiros' && scope.parceiros && <ParceirosPainel scope={scope} />}
                {scope.aba === 'consultas' && scope.consultas && <ConsultasPainel scope={scope} />}
            </div>
        )
    }

    renderFiltros() {
        const { scope } = this
        const { classes } = this.styles
        const hoje = hojeEmSaoPaulo()
        const geradoEm = {
            negocios: scope.negocios?.geradoEm,
            parceiros: scope.parceiros?.geradoEm,
            operacao: scope.operacao?.geradoEm,
            consultas: scope.consultas?.geradoEm
        }[scope.aba]
        const gerado = geradoEm ? new Date(geradoEm).toLocaleTimeString('pt-BR', { timeStyle: 'short' }) : undefined

        return (
            <Box className={classes.filtros}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={scope.atalho}
                    onChange={this.emitAtalhoChange}
                    aria-label="Período"
                >
                    {(Object.keys(ROTULO_ATALHO) as Atalho[]).map((atalho) => (
                        <ToggleButton key={atalho} value={atalho} sx={{ px: 1.5, textTransform: 'none' }}>
                            {ROTULO_ATALHO[atalho]}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                {scope.atalho === 'livre' ? (
                    <>
                        <TextField
                            className={classes.data}
                            size="small"
                            type="date"
                            label="De"
                            value={scope.de}
                            onChange={this.emitDeChange}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: hoje }}
                        />
                        <TextField
                            className={classes.data}
                            size="small"
                            type="date"
                            label="Até"
                            value={scope.ate}
                            onChange={this.emitAteChange}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: hoje }}
                        />
                    </>
                ) : (
                    scope.de && (
                        <Typography color="text.secondary" fontSize={14}>
                            {formatarDia(scope.de, true)} a {formatarDia(scope.ate, true)}
                        </Typography>
                    )
                )}

                {scope.aba === 'negocios' && (
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={scope.origem}
                        onChange={this.emitOrigemChange}
                        aria-label="Origem dos negócios"
                    >
                        <ToggleButton value="todos" sx={{ px: 1.5, textTransform: 'none' }}>
                            Todos os negócios
                        </ToggleButton>
                        <ToggleButton value="lia" sx={{ px: 1.5, textTransform: 'none' }}>
                            Criados pela LIA
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}

                <Box className={classes.atualizado}>
                    {gerado && (
                        <Typography color="text.secondary" fontSize={13}>
                            Atualizado às {gerado}
                        </Typography>
                    )}
                    <Button
                        size="small"
                        startIcon={<PictureAsPdfOutlinedIcon />}
                        onClick={this.emitBaixarPdf}
                        disabled={scope.carregando || scope.gerandoPdf || !this.temPainel()}
                    >
                        {scope.gerandoPdf ? 'Gerando PDF…' : 'Baixar PDF'}
                    </Button>
                    <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={this.emitAtualizar}
                        disabled={scope.carregando}
                    >
                        Atualizar
                    </Button>
                </Box>
            </Box>
        )
    }
}

export const DashboardsView = classToFComponent(DashboardsClass, React)
export default DashboardsView
